import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Grid, Paper, TableContainer, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { AlertDialog } from '../../../src/components/core/AlertDialog';
import { LocalUser } from '../../../src/types/app/localUser.types';
import * as api from '../../api/app/localUserApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import DataGridTable from '../../components/core/DataGridTable/DataGridTable';
import { Loader } from '../../components/core/Loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';

interface Props {
  isReadOnly: boolean;
}
export function LocalUserList({ isReadOnly }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const { data, isLoading, isError } = useQuery<LocalUser[]>('localUser', api.getList);
  const { mutate: mutateDeleteLocalUser } = useMutation(api.deleteItem);

  const columns = [
    { headerName: t('page.local-user.table-col-1'), field: 'id', width: 150 },
    { headerName: t('page.local-user.table-col-2'), field: 'name', width: 200 },
    { headerName: t('page.local-user.table-col-3'), field: 'email', width: 200 },
    {
      field: '',
      sortable: false,
      disableColumnMenu: true,
      hide: isReadOnly,
      minWidth: 150,
      flex: 1,
      align: 'right',
      renderCell: (params: any) => {
        return (
          <>
            {!isReadOnly && (
              <>
                <Tooltip title={t('general.action-buttons.edit')}>
                  <Button onClick={() => handleOnEdit(params.id)}>
                    <EditIcon />
                  </Button>
                </Tooltip>
                <Tooltip title={t('general.action-buttons.delete')}>
                  <Button onClick={() => handleOnDelete(params.id)}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
  ];

  const handleOnEdit = (id: number) => navigate(`/localUser/detail/${id}`);
  const handleOnCreate = () => navigate(`/localUser/create`);
  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteLocalUser(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(t('page.local-user.item-deleted'), { variant: 'success' });
        queryClient.invalidateQueries(['localUser']);
        confirmDeleteAttempt();
      },
      onError: (result) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;
  return (
    <>
      <Grid item xs={8}>
        <TableContainer component={Paper}>
          <div style={{ height: '70vh', width: '100%' }}>
            <DataGridTable
              height={'70vh'}
              data={data}
              columns={columns}
              fulltextSearch={true}
              autoPageSize={true}
            />
          </div>
          {!isReadOnly && (
            <Tooltip title={t('general.action-buttons.new')}>
              <Button onClick={handleOnCreate}>
                <AddIcon />
              </Button>
            </Tooltip>
          )}
        </TableContainer>
      </Grid>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
